/**
 * VARIATION_SUMMARY.JSX - Variation Totals Table Component
 * 
 * SUMMARY:
 * Presentational component for displaying variation totals in the variation-specific layout.
 * Shows cost, profit, and sale rows for material, resource, contractor, and total columns.
 * 
 * PROPS:
 * @param {Object} task - Task data with totals
 * @param {Object} fields - Form fields (profit margin)
 * @param {boolean} editMode - Whether in edit mode
 * @param {Object} taskState - Task state (lock icon)
 * @param {Function} inputChangeHandler - Input change handler
 * @param {Function} profitMarginDialogHandler - Profit margin dialog handler
 * 
 * @returns {JSX.Element} Totals table
 */

import React from 'react';
import Table from 'react-bootstrap/Table';
import { toPercent, toUSD, numberFormat } from "../../../../util/currency-formatter";
import idx from 'idx';

export default function VariationSummary({
    task,
    fields,
    editMode,
    taskState,
    inputChangeHandler,
    profitMarginDialogHandler
}) {
    return (
        <div className="col-12 variation-table-container mt-2" data-cy="total-table">
            <div className="row g-0">
                {/* Empty space to align with description column */}
                <div className="col-lg-4 col-md-12"></div>

                {/* Totals Table (Right Column) - matches col-lg-7 of SubTask table */}
                <div className="col-lg-7 col-md-11 col-sm-11 Variation-summary-table">
                    <Table bordered className="table-create mb-0 tbl-fixed tbl-fixed-width variation-table variation-table-subtask" data-cy="variation-table">
                        <tbody>
                            {/* Header Row */}
                            <tr>
                                <th></th>
                                <th data-cy="material"><strong>Material</strong></th>
                                <th data-cy="contractor"><strong>Contractor</strong></th>
                                <th data-cy="resource"><strong>Resource</strong></th>
                                <th data-cy="planned-effort"><strong>Planned Effort</strong></th>
                                <th data-cy="total"><strong>Total</strong></th>
                            </tr>

                            {/* Cost Row */}
                            <tr>
                                <td className="fw-bold" data-cy="cost">
                                    <strong>Cost</strong>
                                </td>
                                <td>
                                    <span className="tableinfo" data-cy="cost-material">
                                        {toUSD(idx(task, _ => _.task.total.material.cost))}
                                    </span>
                                </td>
                                <td>
                                    <span className="tableinfo" data-cy="cost-contractor">
                                        {toUSD(idx(task, _ => _.task.total.subcontractor.cost))}
                                    </span>
                                </td>
                                <td>
                                    <span className="tableinfo" data-cy="cost-resource">
                                        {toUSD(idx(task, _ => _.task.total.labor.cost))}
                                    </span>
                                </td>
                                <td>
                                    <span className="tableinfo" data-cy="cost-planned-effort">
                                        {numberFormat(idx(task, _ => _.task.total.forecastHours) || 0, true)} hrs
                                    </span>
                                </td>
                                <td>
                                    <span className="tableinfo" data-cy="cost-total">
                                        {toUSD(idx(task, _ => _.task.total.subtotal))}
                                    </span>
                                </td>
                            </tr>

                            {/* Profit Row */}
                            <tr>
                                <td className="fw-bold" data-cy="profit">
                                    <strong>Profit</strong>
                                </td>
                                <td>
                                    <div data-cy="profit-material">
                                        ({toPercent(idx(task, _ => _.task.total.material.profit), idx(task, _ => _.task.total.material.cost))}) {toUSD(idx(task, _ => _.task.total.material.profit))}
                                    </div>
                                </td>
                                <td>
                                    <div data-cy="profit-contractor">
                                        ({toPercent(idx(task, _ => _.task.total.subcontractor.profit), idx(task, _ => _.task.total.subcontractor.cost))}) {toUSD(idx(task, _ => _.task.total.subcontractor.profit))}
                                    </div>
                                </td>
                                <td>
                                    <div data-cy="profit-resource">
                                        ({toPercent(idx(task, _ => _.task.total.labor.profit), idx(task, _ => _.task.total.labor.cost))}) {toUSD(idx(task, _ => _.task.total.labor.profit))}
                                    </div>
                                </td>
                                <td></td>
                                <td>
                                    <div data-cy="profit-total">
                                        ({toPercent(idx(task, _ => _.task.total.total_profit), idx(task, _ => _.task.total.subtotal))}) {toUSD(idx(task, _ => _.task.total.total_profit))}
                                    </div>
                                </td>
                            </tr>

                            {/* Sale Row */}
                            <tr>
                                <td className="fw-bold sale-label" data-cy="sale">
                                    Sale
                                </td>
                                <td>
                                    <strong className="tableinfo" data-cy="sale-material">
                                        {toUSD(idx(task, _ => _.task.total.material.sale))}
                                    </strong>
                                </td>
                                <td>
                                    <strong className="tableinfo" data-cy="sale-contractor">
                                        {toUSD(idx(task, _ => _.task.total.subcontractor.sale))}
                                    </strong>
                                </td>
                                <td>
                                    <strong className="tableinfo" data-cy="sale-resource">
                                        {toUSD(idx(task, _ => _.task.total.labor.sale))}
                                    </strong>
                                </td>
                                <td></td>
                                <td>
                                    <strong className="tableinfo" data-cy="sale-total">
                                        {toUSD(idx(task, _ => _.task.total.total))}
                                    </strong>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
                <div className="col-lg-1"></div>
            </div>
        </div>
    );
}

